'use client'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'

import { Breadcrumbs } from '@/components/breadcrumbs'

import { EASE, Eyebrow, HAIRLINE, MODULES, UDC, useReveal } from './shared'

/* The hero states the page's whole argument in its structure: the headline
   rests on a six-cell hairline rail, and the dividers between those cells
   grow up into the hero field as columns. The module index is literally
   holding up the roof. No mesh glow, no ribbon field, no floating cards —
   the homepage owns those; this page owns the rail. */

export function UdcHero() {
  const rail = useReveal<HTMLDivElement>(0.25)

  return (
    <section
      className={cn(
        'relative overflow-hidden hero-adaptive-bg',
        // The site header is transparent until scrolled, so the hero canvas
        // has to start at the true top of the viewport — same offset trick as
        // the homepage hero.
        'mt-[calc(var(--header-h)*-1)]'
      )}
    >
      {/* Circuit backdrop. --circuit-bg already resolves to emerald traces in
          light and mint in dark; the radial mask makes sure it dissolves well
          before the rail rather than ending on a hard line. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
          maskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
        }}
      />

      <div className="container relative z-10 mx-auto">
        <div className="flex min-h-[82svh] flex-col justify-center pb-14 pt-[calc(var(--header-h)+4rem)] sm:pb-20">
          <Breadcrumbs
            items={[
              { name: 'Home', href: '/' },
              { name: 'Products', href: '/products' },
              { name: UDC.title, href: `/products/${UDC.slug}` },
            ]}
          />

          <h1
            className={cn(
              'font-heading mt-6 max-w-4xl font-normal tracking-tight text-foreground',
              // Line height pinned to each step with the `/` shorthand — a
              // bare leading-* loses to the one baked into text-5xl/6xl.
              'text-[34px]/[1.12] sm:text-5xl/[1.12] md:text-6xl/[1.12] lg:text-[66px]/[1.1]'
            )}
          >
            {/* The headline used to read "Every system your campus runs. Under
                one roof." — true to the page, but it named neither the product
                nor the category anyone searches for. The name and the term now
                open it; the underlined phrase is unchanged apart from its
                capital. */}
            <ChromaText>
              {UDC.title}: campus ERP software with every system{' '}
              <span
                className={cn(
                  'relative inline-block',
                  'after:absolute after:left-0 after:h-[3px] after:w-full after:rounded-full after:bg-[#37ecb0]',
                  'after:-bottom-1.5 sm:after:-bottom-2.5'
                )}
              >
                under one roof.
              </span>
            </ChromaText>
          </h1>

          <p className="mt-7 max-w-2xl text-base leading-relaxed text-[#2B3A46] sm:text-lg dark:text-[#DEE9EE]/90">
            Unified Digital Campus is the roof over HRMS, Payroll, Accounts,
            LMS, cloud and AI: one datastore, one identity, one audit trail.
            Admissions to alumni, salary run to board review, on a single
            synchronised record.
          </p>

          <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Button asChild size="xl">
              <a href="/schedule-meeting">Request a demo</a>
            </Button>

            <Button variant="outline" asChild size="xl">
              <a href="#specification">See what ships</a>
            </Button>
          </div>
        </div>
      </div>

      {/* ── The rail ──────────────────────────────────────────────────────
          Full-bleed, hairline-ruled, six cells. Built with a 1px grid gap
          over a border-coloured backing rather than per-cell borders, so the
          dividers stay exact at every breakpoint and no stray edge line
          appears when the row wraps. */}
      <div ref={rail.ref} className="relative z-10">
        {/* Columns growing up out of the dividers into the hero field. Only
            at lg, where the rail is genuinely six across and the divider
            positions are predictable sixths. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-full hidden h-32 lg:block xl:h-40"
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <span
              key={n}
              className="absolute bottom-0 top-0 w-px origin-bottom bg-gradient-to-t from-border/90 to-transparent"
              style={{
                left: `${(n / 6) * 100}%`,
                transform: rail.shown ? 'scaleY(1)' : 'scaleY(0)',
                transition: `transform 700ms ${EASE} ${n * 90}ms`,
              }}
            />
          ))}
        </div>

        <div className={cn('border-y', HAIRLINE)}>
          <div className="grid grid-cols-2 gap-px bg-border/70 sm:grid-cols-3 lg:grid-cols-6">
            {MODULES.map((m, i) => (
              <div
                key={m.key}
                className="bg-background px-4 py-5 sm:px-5 sm:py-6"
                style={{
                  opacity: rail.shown ? 1 : 0,
                  transform: rail.shown ? 'none' : 'translateY(10px)',
                  transition: `opacity 500ms ${EASE} ${i * 70}ms, transform 500ms ${EASE} ${i * 70}ms`,
                }}
              >
                <div className="font-heading text-base font-normal leading-none text-foreground sm:text-lg">
                  {m.name}
                </div>
                <div className="mt-2 text-[11px] leading-snug text-muted-foreground sm:text-xs">
                  {m.line}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
